import React from 'react';
import { createRoot } from 'react-dom/client';

// Global alert queue and container management
let alertQueue = [];
let isProcessing = false;
let alertContainer = null;
let alertRoot = null;

const createAlertContainer = () => {
  if (!alertContainer) {
    alertContainer = document.createElement('div');
    alertContainer.id = 'alert-container';
    alertContainer.style.cssText = `
      position: fixed;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      z-index: 9999;
      pointer-events: none;
      display: flex;
      align-items: center;
      justify-content: center;
    `;
    document.body.appendChild(alertContainer);
    alertRoot = createRoot(alertContainer);
  }
  return alertRoot;
};

const cleanupAlertContainer = () => {
  if (alertContainer && alertContainer.parentNode) {
    try {
      alertRoot.unmount();
    } catch (error) {
      console.warn('Alert root unmount error:', error);
    }
    alertContainer.parentNode.removeChild(alertContainer);
    alertContainer = null;
    alertRoot = null;
  }
};

const processAlertQueue = async () => {
  if (isProcessing || alertQueue.length === 0) return;
  
  isProcessing = true;
  const alert = alertQueue.shift();
  
  try {
    // Dynamically import AlertModal
    const { default: AlertModal } = await import('../Components/Alert/AlertModal');
    const root = createAlertContainer();
    
    const handleClose = () => {
      try {
        root.unmount();
        cleanupAlertContainer();
      } catch (error) {
        console.warn('Alert cleanup error:', error);
        cleanupAlertContainer();
      }
      
      // Process next alert after a short delay
      setTimeout(() => {
        isProcessing = false;
        processAlertQueue();
      }, 100);
    };

    root.render(
      React.createElement(AlertModal, {
        isOpen: true,
        message: alert.message,
        type: alert.type,
        onClose: handleClose,
        duration: alert.duration
      })
    );
  } catch (error) {
    console.warn('Alert processing error:', error);
    cleanupAlertContainer();
    isProcessing = false;
    processAlertQueue();
  }
};

const showAlert = (message, type = 'default', duration = 1500) => {
  // Add to queue
  alertQueue.push({ message, type, duration });
  
  // Process queue
  processAlertQueue();
};

const alertService = {
  success: (message, duration = 1500) => {
    showAlert(message, 'success', duration);
  },
  
  error: (message, duration = 1500) => {
    showAlert(message, 'error', duration);
  },
  
  warning: (message, duration = 2000) => {
    showAlert(message, 'warning', duration);
  },
  
  info: (message, duration = 2000) => {
    showAlert(message, 'info', duration);
  },
  
  default: (message, duration = 2000) => {
    showAlert(message, 'default', duration);
  }
};

export default alertService;